import useSessionNotice from '@/hooks/useSessionNotice';
import { UploadSlot, requestUploadSlots } from '@/util/uploadSlots';
import { useCallback } from 'react';

/**
 * The single entry point for the `POST /resources` step of every upload.
 *
 * Wraps `requestUploadSlots` with the two things a call site should not have
 * to remember:
 *
 *  - the SESSION GUARD. Every upload identifies itself with the WebSocket
 *    session id, and that id does not exist until the socket's `connected`
 *    event arrives. Nothing in the UI blocks a drop before then — the header
 *    stays silent while `connectionStatus` is 'connecting', deliberately, so a
 *    normal page load does not flash a warning — so a fast drop used to send
 *    `x-session-id: ''` and fail somewhere in the backend instead. Refusing
 *    here keeps that failure on this side of the wire, where it can be
 *    explained.
 *
 *  - the ACKNOWLEDGEMENT. Refused files are reported once for the whole batch.
 *
 * Returns only the granted slots, so a caller that ignores failures still
 * cannot build a row for a file the backend refused. An empty array means
 * "nothing to do" for every reason — no files, no session, or none granted.
 */
const useUploadSlots = () => {
  const { notifyNotConnected, notifyUploadRefused } = useSessionNotice();

  return useCallback(
    async (sessionId: string, files: File[]): Promise<UploadSlot[]> => {
      if (!files.length) return [];
      if (!sessionId) {
        notifyNotConnected();
        return [];
      }
      const { granted, rejectedNames } = await requestUploadSlots(
        sessionId,
        files
      );
      notifyUploadRefused(rejectedNames);
      return granted;
    },
    [notifyNotConnected, notifyUploadRefused]
  );
};

export default useUploadSlots;
